import React from 'react';
import { Package, CheckCircle, Truck, ClipboardList, ArrowDownUp, Bookmark, AlertTriangle, Clock } from 'lucide-react';
import { OrderTimelineSummaryDto } from '../../../../../types/orderTimeline';

interface TimelineSummaryProps {
  summary: OrderTimelineSummaryDto;
}

export const TimelineSummary: React.FC<TimelineSummaryProps> = ({ summary }) => {
  const cards = [
    {
      label: 'Ordrelinjer',
      value: summary.totalOrderLines,
      icon: Package,
      color: 'text-blue-600 bg-blue-50'
    },
    {
      label: 'Bestilt',
      value: summary.totalQuantityOrdered,
      icon: ClipboardList,
      color: 'text-indigo-600 bg-indigo-50'
    },
    {
      label: 'Plukket',
      value: summary.totalQuantityPicked,
      icon: CheckCircle,
      color: 'text-green-600 bg-green-50'
    },
    {
      label: 'Levert',
      value: summary.totalQuantityDelivered,
      icon: Truck,
      color: 'text-purple-600 bg-purple-50'
    },
    {
      label: 'Plukklister',
      value: summary.relatedPickingLists,
      icon: ClipboardList,
      color: 'text-orange-600 bg-orange-50'
    },
    {
      label: 'Transaksjoner',
      value: summary.totalStockTransactions,
      icon: ArrowDownUp,
      color: 'text-cyan-600 bg-cyan-50'
    },
    {
      label: 'Reservasjoner',
      value: summary.totalReservations,
      icon: Bookmark,
      color: 'text-yellow-600 bg-yellow-50'
    },
    {
      label: 'Problemer',
      value: summary.problemsDetected,
      icon: AlertTriangle,
      color: summary.problemsDetected > 0 ? 'text-red-600 bg-red-50' : 'text-gray-400 bg-gray-50'
    }
  ];

  return (
    <div className="mb-4">
      <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.label}
              className="bg-white rounded-lg border border-gray-200 p-2 text-center"
            >
              <div className={`inline-flex items-center justify-center w-7 h-7 rounded-full mb-1 ${card.color}`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className="text-lg font-bold text-gray-900">{card.value}</div>
              <div className="text-xs text-gray-500">{card.label}</div>
            </div>
          );
        })}
      </div>

      {/* Pick completion bar */}
      <div className="mt-2 bg-white rounded-lg border border-gray-200 p-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-500 font-medium">Plukk-fremgang</span>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="font-semibold text-gray-900">{summary.pickCompletionPercent.toFixed(1)}%</span>
            {summary.pickingDurationMinutes != null && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {summary.pickingDurationMinutes < 60
                  ? `${summary.pickingDurationMinutes.toFixed(0)} min`
                  : `${(summary.pickingDurationMinutes / 60).toFixed(1)} timer`
                }
              </span>
            )}
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              summary.pickCompletionPercent >= 100 ? 'bg-green-500' :
              summary.pickCompletionPercent >= 50 ? 'bg-blue-500' :
              summary.pickCompletionPercent > 0 ? 'bg-yellow-500' :
              'bg-gray-300'
            }`}
            style={{ width: `${Math.min(100, summary.pickCompletionPercent)}%` }}
          />
        </div>
      </div>
    </div>
  );
};
